import { Button as AntButton, Modal, Select } from 'antd';
import _ from 'lodash';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getExamConditions, updateExamConditions } from '../../api/placeTest.api';
import { getTestTypes } from '../../api/testCenter.api';
import useAxios from '../../hooks/useAxios';
import useNotification from '../../hooks/useNotification';
import { IExamineeDetail, IGetExamConditions } from '../../models/placeTest.model';
import { IGetTestTypes, ITestType } from '../../models/testCenter.model';
import ErrorResponse from '../ErrorResponse';
import Loader from '../Loader';
import Radio from '../Radio';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  data: IExamineeDetail|undefined;
  platScheduleCode: number|undefined;
  doGetExamineeDetail: (platRequireCode: number) => void;
  doGetEntranceDetail: () => void;
};

const InputExamInfoModal: FC<IProps> = ({
  visible,
  setVisible,
  data,
  platScheduleCode,
  doGetExamineeDetail,
  doGetEntranceDetail,
}) => {
  const { t } = useTranslation();
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetExamConditions>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetTestTypes>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();

  const [eduStepCode, setEduStepCode] = useState('');
  const [surveyRequiredYn, setSurveyRequiredYn] = useState<string>();
  const [prtRequiredYn, setPrtRequiredYn] = useState<string>();
  const [oceanRequiredYn, setOceanRequiredYn] = useState<string>();
  const [prtExamTypeCode, setPrtExamTypeCode] = useState<number>();
  const [ptExamTypeCode, setPtExamTypeCode] = useState<number>();
  const [prts, setPrts] = useState<ITestType[]>([]);
  const [predigerRequiredYn, setPredigerRequiredYn] = useState<string>();
  const [pts, setPts] = useState<ITestType[]>([]);

  useEffect(() => {
    if (data?.baseInfo?.platRequireCode) {
      execute(getExamConditions(data.baseInfo.platRequireCode));
    }
  }, [data]);

  useEffect(() => {
    if (res?.data) {
      if (res.data.eduStepCode === 'EM') {
        setSurveyRequiredYn(res.data.surveyRequiredYn);
        setPrtRequiredYn(res.data.prtRequiredYn);
        setOceanRequiredYn(res.data.oceanRequiredYn);
        setPrtExamTypeCode(res.data.prtExamTypeCode);  
      } else {
        setPredigerRequiredYn(res.data.predigerRequiredYn);
      }
      setEduStepCode(res.data.eduStepCode);
      setPtExamTypeCode(res.data.platExamTypeCode);
    }
  }, [res]);

  useEffect(() => {
    execute2(getTestTypes());
  }, []);

  useEffect(() => {
    if (res2) {
      const _data: ITestType[] = _.cloneDeep(res2.data);
      const _prts = _data.filter((item) => item.examDiv === 'PR');
      setPrts(_prts);
      const _pts = _data.filter((item) => item.examDiv === 'PT');
      setPts(_pts);
    }
  }, [res2]);

  useEffect(() => {
    if (submitRes) {
      if (data?.baseInfo?.platRequireCode) {
        doGetExamineeDetail(data.baseInfo.platRequireCode);
      }
      doGetEntranceDetail();
      successNotification(t('common.message.save1'));
      setVisible(false);
    }
  }, [submitRes]);

  const onSubmit = () => {
    if (!data?.baseInfo?.platRequireCode || !platScheduleCode) return;

    let req: any = {};

    if (eduStepCode === 'EM') {
      if (!surveyRequiredYn) {
        return infoNotification(t('entrance.schedule.message7'));
      }
      if (!prtRequiredYn) {
        return infoNotification(t('entrance.schedule.message8'));
      }
      if (!oceanRequiredYn) {
        return infoNotification(t('entrance.schedule.message9'));
      }
      if (surveyRequiredYn === 'N' && prtRequiredYn === 'Y' && !prtExamTypeCode) {
        return infoNotification(t('entrance.schedule.message10'));
      }
      if (prtRequiredYn === 'N' && !ptExamTypeCode) {
        return infoNotification(t('entrance.schedule.message11'));
      }

      req = {
        platScheduleCode,
        memberCode: data?.baseInfo?.memberCode,
        surveyRequiredYn,
        prtRequiredYn,
        oceanRequiredYn,
        examTypeCode: prtRequiredYn === 'Y' ? prtExamTypeCode : ptExamTypeCode,
      };
    } else {
      if (!predigerRequiredYn) {
        return infoNotification(t('common.exam.1'));
      }
      if (!ptExamTypeCode) {
        return infoNotification(t('common.exam.2'));
      }

      req = {
        platScheduleCode,
        memberCode: data?.baseInfo?.memberCode,
        oceanRequiredYn: predigerRequiredYn,
        examTypeCode: ptExamTypeCode,
      };
    }
    
    submitExecute(updateExamConditions(data.baseInfo.platRequireCode, req));
  }

  return (
    <>
      {(isLoading || isLoading2 || submitIsLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={submitError} />

      <Modal
        title={t('entrance.schedule.title7')}
        width={600}
        visible={visible}
        onCancel={() => setVisible(false)}

        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={onSubmit}
            >
              {t('common.button.save')}
            </AntButton>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={() => setVisible(false)}
            >
              {t('common.button.close')}
            </AntButton>
          </div>
        ]}
      >
        <div className='comm-table__area mt-0'>
          <table>
            <thead>
              <tr>
                <th>{t('entrance.schedule.column34')}</th>
                <th>{t('entrance.schedule.column35')}</th>
                <th>{t('entrance.schedule.column36')}</th>
              </tr>
            </thead>
            <tbody>
              {
                eduStepCode === 'EM'
                  ? (
                    <>
                      <tr>
                        <td>{t('entrance.schedule.button8')}</td>
                        <td>
                          <Radio
                            checked={surveyRequiredYn === 'Y'}
                            onChange={() => setSurveyRequiredYn('Y')}
                            text="Y"
                            disabled={Boolean(data?.testInfo?.surveyScore)}
                          />
                          <Radio
                            className="ml-4"
                            checked={surveyRequiredYn === 'N'}
                            onChange={() => setSurveyRequiredYn('N')}
                            text="N"
                            disabled={Boolean(data?.testInfo?.surveyScore)}
                          />
                        </td>
                        <td />
                      </tr>
                      <tr>
                        <td>Pre Test</td>
                        <td>
                          <Radio
                            checked={prtRequiredYn === 'Y'}
                            onChange={() => setPrtRequiredYn('Y')}
                            text="Y"
                            disabled={data?.testInfo?.prtCompleteYn === 'Y'}
                          />
                          <Radio
                            className="ml-4"
                            checked={prtRequiredYn === 'N'}
                            onChange={() => setPrtRequiredYn('N')}
                            text="N"
                            disabled={data?.testInfo?.prtCompleteYn === 'Y'}
                          />
                        </td>
                        <td>
                          <Select
                            className="w-100"
                            placeholder={t('common.button.select')}
                            value={prtExamTypeCode}
                            onChange={value => setPrtExamTypeCode(value)}
                            disabled={data?.testInfo?.prtCompleteYn === 'Y' || prtRequiredYn === 'N'}
                          >
                            {prts?.map((item: any) => {
                              return (
                                <Select.Option value={item.examTypeCode}>
                                  {item.examTypeName}
                                </Select.Option>
                              );
                            })}
                          </Select>
                        </td>
                      </tr>
                      <tr>
                        <td>OCEAN</td>
                        <td>
                          <Radio
                            checked={oceanRequiredYn === 'Y'}
                            onChange={() => setOceanRequiredYn('Y')}
                            text="Y"
                            disabled={data?.testInfo?.oceanCompleteYn === 'Y'}
                          />
                          <Radio
                            className="ml-4"
                            checked={oceanRequiredYn === 'N'}
                            onChange={() => setOceanRequiredYn('N')}
                            text="N"
                            disabled={data?.testInfo?.oceanCompleteYn === 'Y'}
                          />
                        </td>
                        <td />
                      </tr>
                    </>
                    )
                  : (
                    <tr>
                      <td>Prediger</td>
                      <td>
                        <Radio
                          checked={predigerRequiredYn === 'Y'}
                          onChange={() => setPredigerRequiredYn('Y')}
                          text="Y"
                          disabled={data?.testInfo?.predigerCompleteYn === 'Y'}
                        />
                        <Radio
                          className="ml-4"
                          checked={predigerRequiredYn === 'N'}
                          onChange={() => setPredigerRequiredYn('N')}
                          text="N"
                          disabled={data?.testInfo?.predigerCompleteYn === 'Y'}
                        />
                      </td>
                      <td />
                    </tr>
                    )
              }
              <tr>
                <td>Placement Test</td>
                <td />
                <td>
                  <Select
                    className="w-100"
                    placeholder={t('common.button.select')}
                    value={ptExamTypeCode}
                    onChange={value => setPtExamTypeCode(value)}
                    disabled={data?.testInfo?.platCompleteYn === 'Y' || prtRequiredYn === 'Y'}
                  >
                    {pts.filter(item => item.eduStepCode === eduStepCode).map((item: any) => (
                      <Select.Option value={item.examTypeCode}>
                        {item.examTypeName}
                      </Select.Option>
                    ))}
                  </Select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Modal>
    </>
  );
}

export default InputExamInfoModal;
